"use client";

import * as React from "react";
import { useLocale, useTranslations } from "next-intl";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import NoDataCard from "@/components/NoDataCard";
import { FullscreenVideo } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ControlTab/Editor/FullscreenVideo";
import { getIconByTitle } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ControlTab/Editor/iconVideo";
import { formatDateTime } from "@/shared/dateFormat";
import { useEnumeration } from "@/api/enums/enumsApi";
import type {
  ReportIdentity,
  ReportViolation,
  ReportWebinarSession,
} from "@/types/endpoints/reports/GetAssignmentReportData";

interface PublicControlTabProps {
  identities: ReportIdentity[];
  videoSessions: ReportWebinarSession[];
  violations: ReportViolation[];
}

/** Video track with multiple sources for concatenated playback */
interface VideoTrack {
  id: string;
  src: string;
  title: string;
  /** Additional video sources to play sequentially after the main one */
  additionalSources?: string[];
  /** Pre-calculated durations for each video source (in seconds) */
  durations?: number[];
}

const getSessionOverlayIcon = (sessionType?: string) => {
  switch (sessionType) {
    case "main-camera":
      return getIconByTitle("Main Camera");
    case "second-camera":
      return getIconByTitle("Secondary Camera");
    case "screen":
      return getIconByTitle("Screen Share");
    default:
      return null;
  }
};

/**
 * Sort sessions by created_at (earliest first)
 */
const sortSessionsByCreatedAt = (sessions: ReportWebinarSession[]): ReportWebinarSession[] => {
  return [...sessions].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateA - dateB;
  });
};

/**
 * Get converted sessions with video URLs, sorted by created_at
 */
const getConvertedSessions = (sessions: ReportWebinarSession[]): ReportWebinarSession[] => {
  return sortSessionsByCreatedAt(sessions).filter(
    (s) => s.state === "converted" && s.record?.video_url
  );
};

/** Represents a single identity image with its URL and type */
interface IdentityImage {
  url: string;
  type: 'screenshot' | 'id' | 'face';
  identityId: number;
}

/**
 * Get all image URLs from an identity object
 * Returns array of images: screenshot field, screenshots.id, and screenshots.face
 */
const getIdentityImages = (identity: ReportIdentity): IdentityImage[] => {
  const images: IdentityImage[] = [];

  // Check direct screenshot field first
  if (identity.screenshot) {
    images.push({ url: identity.screenshot, type: 'screenshot', identityId: identity.id });
  }

  // Check screenshots object (not array)
  if (identity.screenshots && !Array.isArray(identity.screenshots)) {
    const screenshots = identity.screenshots as { id?: string; face?: string };
    // Add 'id' (full photo with ID document)
    if (screenshots.id) {
      images.push({ url: screenshots.id, type: 'id', identityId: identity.id });
    }
    // Add 'face' (cropped face photo)
    if (screenshots.face) {
      images.push({ url: screenshots.face, type: 'face', identityId: identity.id });
    }
  }

  return images;
};

/**
 * Get the primary image URL from a violation object
 * Prefer screenshot, fallback to first screenshots[] item
 */
const getViolationImageUrl = (violation: ReportViolation): string | null => {
  if (typeof violation.screenshot === "string" && violation.screenshot.length > 0) {
    return violation.screenshot;
  }

  if (Array.isArray(violation.screenshots) && violation.screenshots.length > 0) {
    const first = violation.screenshots[0] as unknown;
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      const candidate = (first as { url?: string; thumb_url?: string; screenshot?: string }).url
        ?? (first as { url?: string; thumb_url?: string; screenshot?: string }).thumb_url
        ?? (first as { url?: string; thumb_url?: string; screenshot?: string }).screenshot;
      if (typeof candidate === "string" && candidate.length > 0) return candidate;
    }
  }

  return null;
};

/**
 * PublicControlTab - displays identity photos and video recordings
 * Matches the design of the original ControlTab component
 *
 * When multiple videos of the same type exist, they are sorted by created_at
 * and played sequentially (earliest first, then concatenated to the end)
 */
export default function PublicControlTab({
  identities,
  videoSessions,
  violations,
}: PublicControlTabProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { data: actionTypes = [] } = useEnumeration("AssignmentActionType");

  const getViolationTitle = React.useCallback(
    (violation: ReportViolation): string => {
      if (violation.description && violation.description.trim().length > 0) {
        return violation.description;
      }

      if (violation.action_type) {
        const normalized = violation.action_type.toLowerCase();
        const found = actionTypes.find(
          (type: { raw: string; name: string }) => type.raw.toLowerCase() === normalized
        );
        if (found?.name) return found.name;
        return violation.action_type.replace(/_/g, " ");
      }

      return t("label-assignment-violations");
    },
    [actionTypes, t]
  );

  const [selectedVideo, setSelectedVideo] = React.useState<VideoTrack | null>(null);

  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);
  const [selectedViolationIndex, setSelectedViolationIndex] = React.useState<number | null>(null);
  const [thumbErrorIds, setThumbErrorIds] = React.useState<Record<string, true>>({});

  const markThumbError = (id: unknown) => {
    const key = String(id);
    setThumbErrorIds((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  // Collect all identity images from all identities (flattened list)
  const allIdentityImages = React.useMemo(() => {
    return identities.flatMap((identity) => getIdentityImages(identity));
  }, [identities]);
  const hasIdentityImages = allIdentityImages.length > 0;

  const sortedViolations = React.useMemo(() => {
    return [...violations].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });
  }, [violations]);
  const hasViolations = sortedViolations.length > 0;

  const violationImages = React.useMemo(() => {
    return sortedViolations
      .map((violation) => {
        const imageUrl = getViolationImageUrl(violation);
        const title = getViolationTitle(violation);

        return imageUrl
          ? {
              url: imageUrl,
              title,
              createdAt: violation.created_at,
              violationId: violation.id,
            }
          : null;
      })
      .filter((item): item is { url: string; title: string; createdAt: string; violationId: number } =>
        Boolean(item)
      );
  }, [sortedViolations, t]);

  // Group video sessions by type and sort by created_at (earliest first)
  const mainCameras = sortSessionsByCreatedAt(
    videoSessions.filter((s) => s.session_type === "main-camera")
  );
  const secondCameras = sortSessionsByCreatedAt(
    videoSessions.filter((s) => s.session_type === "second-camera")
  );
  const screenRecords = sortSessionsByCreatedAt(
    videoSessions.filter((s) => s.session_type === "screen")
  );

  // Get first video of each type for display (show all, even if not converted yet)
  const firstVideos = [mainCameras[0], secondCameras[0], screenRecords[0]]
    .filter(Boolean);

  const hasAnyVideos = firstVideos.length > 0;

  /**
   * Handle video click - collect all converted videos of the same type
   * and pass them for sequential playback
   */
  const handleVideoClick = (session: ReportWebinarSession) => {
    // Get all converted sessions of the same type
    let sessionsOfType: ReportWebinarSession[] = [];
    switch (session.session_type) {
      case "main-camera":
        sessionsOfType = getConvertedSessions(mainCameras);
        break;
      case "second-camera":
        sessionsOfType = getConvertedSessions(secondCameras);
        break;
      case "screen":
        sessionsOfType = getConvertedSessions(screenRecords);
        break;
    }

    // If no converted sessions, don't open
    if (sessionsOfType.length === 0) return;

    // First session is the main video, rest are additional sources
    const [firstSession, ...restSessions] = sessionsOfType;
    const additionalSources = restSessions
      .map((s) => s.record?.video_url)
      .filter((url): url is string => Boolean(url));

    // Collect durations for all videos
    const durations = sessionsOfType.map((s) => s.record?.video_duration || 0);

    setSelectedVideo({
      id: String(firstSession.id),
      src: firstSession.record!.video_url,
      title: session.session_type,
      additionalSources: additionalSources.length > 0 ? additionalSources : undefined,
      durations: durations.length > 0 ? durations : undefined,
    });
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  // Identity photo viewer modal
  const renderIdentityViewer = () => {
    if (selectedImageIndex === null) return null;

    const image = allIdentityImages[selectedImageIndex];
    if (!image?.url) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={() => setSelectedImageIndex(null)}
      >
        <button
          onClick={() => setSelectedImageIndex(null)}
          className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 z-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="relative max-w-4xl max-h-[90vh] w-full h-full">

          {/* Navigation buttons */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
            <button
              disabled={selectedImageIndex <= 0}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex(selectedImageIndex - 1);
              }}
              className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg disabled:opacity-40"
            >
              ←
            </button>
            <span className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg text-sm">
              {selectedImageIndex + 1} / {allIdentityImages.length}
            </span>
            <button
              disabled={selectedImageIndex >= allIdentityImages.length - 1}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex(selectedImageIndex + 1);
              }}
              className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg disabled:opacity-40"
            >
              →
            </button>
          </div>

          <div
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={image.url}
              alt={`Identity photo ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full w-auto h-auto object-contain"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderViolationViewer = () => {
    if (selectedViolationIndex === null) return null;

    const image = violationImages[selectedViolationIndex];
    if (!image?.url) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={() => setSelectedViolationIndex(null)}
      >
        <button
          onClick={() => setSelectedViolationIndex(null)}
          className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 z-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
            <button
              disabled={selectedViolationIndex <= 0}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedViolationIndex(selectedViolationIndex - 1);
              }}
              className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg disabled:opacity-40"
            >
              ←
            </button>
            <span className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg text-sm">
              {selectedViolationIndex + 1} / {violationImages.length}
            </span>
            <button
              disabled={selectedViolationIndex >= violationImages.length - 1}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedViolationIndex(selectedViolationIndex + 1);
              }}
              className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg disabled:opacity-40"
            >
              →
            </button>
          </div>

          <div
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={image.url}
              alt={image.title}
              className="max-w-full max-h-full w-auto h-auto object-contain"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Identity Photo Viewer Modal */}
      {renderIdentityViewer()}

      {/* Violations Viewer Modal */}
      {renderViolationViewer()}

      {/* Fullscreen Video Player */}
      {selectedVideo && (
        <FullscreenVideo
          track={selectedVideo}
          onClose={handleCloseVideo}
        />
      )}

      <div className="space-y-4">

        {/* Identity Photos Card */}
        <Card className="gap-2 shadow-none print-no-shadow">
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t("label-proctoring-photo-head-identity")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hasIdentityImages ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {allIdentityImages.map((image, i) => (
                  <div
                    key={`${image.identityId}-${image.type}`}
                    className="relative overflow-hidden rounded-md border bg-muted/40 cursor-pointer transition-shadow"
                    onClick={() => setSelectedImageIndex(i)}
                  >
                    <img
                      src={image.url}
                      alt={t("label-photo-number", { number: i + 1 })}
                      width={300}
                      height={200}
                      className="object-cover w-full h-24"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <NoDataCard className="m-0" />
            )}
          </CardContent>
        </Card>

        {/* Video Recordings Card */}
        {hasAnyVideos && (
          <Card className="gap-2 shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                {t("label-main-records")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {firstVideos.map((session) => (
                  <div
                    key={session.id}
                    className="relative overflow-hidden rounded-md border bg-muted/40 cursor-pointer transition-shadow"
                    onClick={() => handleVideoClick(session)}
                  >
                    {(() => {
                      const Icon = getSessionOverlayIcon(session.session_type);
                      const thumbUrl = session.record?.thumb_url;
                      const key = String(session.id);
                      const canShowThumb =
                        typeof thumbUrl === "string" && thumbUrl.length > 0 && !thumbErrorIds[key];

                      const Overlay = Icon ? (
                        <div
                          className={[
                            "pointer-events-none absolute left-2 top-2",
                            canShowThumb ? "text-white" : "text-black/70",
                          ].join(" ")}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                      ) : null;

                      if (!canShowThumb) {
                        return (
                          <>
                            {Overlay}
                            <div className="w-full h-24 bg-muted/50" />
                          </>
                        );
                      }

                      // Rotate second camera 90 degrees to the right
                      const isSecondCamera = session.session_type === "second-camera";

                      return (
                        <>
                          {Overlay}
                          <img
                            src={thumbUrl}
                            alt={session.session_type}
                            width={300}
                            height={200}
                            className={`object-cover w-full h-24 ${isSecondCamera ? "rotate-90" : ""}`}
                            onError={() => markThumbError(session.id)}
                          />
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Violations Card */}
        <Card className="gap-2 shadow-none print-no-shadow">
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {t("label-assignment-violations")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {t("label-assignment-violations-subtitle")}
            </p>
          </CardHeader>
          <CardContent>
            {hasViolations ? (
              <div className="divide-y">
                {sortedViolations.map((violation) => {
                  const imageUrl = getViolationImageUrl(violation);
                  const title = getViolationTitle(violation);
                  const imageIndex = violationImages.findIndex(
                    (item) => item.violationId === violation.id && item.url === imageUrl
                  );

                  return (
                    <div key={violation.id} className="flex items-start gap-4 py-4">
                      {imageUrl && (
                        <div
                          className="h-16 w-28 rounded-lg overflow-hidden bg-muted/50 border shrink-0"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (imageIndex < 0) return;
                            setSelectedViolationIndex(imageIndex);
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={title}
                            className="object-cover w-full h-full"
                          />
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold text-foreground">
                          {title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateTime(violation.created_at, locale)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <NoDataCard className="m-0" />
            )}
          </CardContent>
        </Card>

        {/* No data state */}
        {!hasIdentityImages && !hasAnyVideos && !hasViolations && (
          <div className="p-6 text-sm text-muted-foreground text-center">
            {t("label-no-proctoring-data")}
          </div>
        )}
      </div>
    </>
  );
}
